import { Toast } from '@byecode/ui'
import type { IndependentAction } from '@lighthouse/core'
import { filter } from 'rambda'

import type { ActionResultADT, FlowNode } from '../../types'
import {
    ClassNode,
    EndNodeType,
    FlowControlNodeType,
    type FlowNodeResultADT,
    FlowRunStatus,
    NodeAndEdgeStatusEnum,
    type NodeInvokeResult,
    TriggerNodeType,
    type Workflow
} from '../../types'
import { buildWorkFlowDataDependencies, FlowExecutor } from '../flowExecutor'
import type { FlowExecutorNode } from '../flowExecutor/type'
import { SyncHook } from '../hooka/SyncHook'
import { ExtraParams } from './helper/common'

type InvokeId = string
type NodeInfo = Map<string, FlowExecutorNode<FlowNode>>
type FlowJobMap = Map<string, NodeInfo>

type FlowNodeInvokeRuntimeParamsMap<E> = Map<string, E>

type ListInvokeOptions<E> = {
    /**
     * 节点执行前的条件判断，返回 false 时跳过执行
     */
    shouldExecute?: (node: IndependentAction, runtimeParams: E) => Promise<boolean> | boolean
    /**
     * 是否在节点失败时中断列表执行，默认不中断
     */
    stopOnError?: boolean
    /**
     * 节点执行后的回调，用于轻量观测（不写入日志）
     */
    afterInvokeNode?: (payload: { actionId: string; result: FlowNodeResultADT | undefined; runtimeParams: E }) => void | Promise<void>
    /**
     * 自定义错误处理
     */
    onError?: (error: unknown, context: { action: IndependentAction; runtimeParams: E }) => void | Promise<void>
}

type Hooks<T extends unknown[] = unknown[]> = {
    initialize: SyncHook<T>
    afterInvoke: SyncHook<T>
    afterInvokeNode: SyncHook<T>
}

const noop = () => void 0

export class ActionWorker<E extends ExtraParams, N, A, K extends keyof N, K2 extends keyof A> {
    /**
     * 动作流执行钩子函数
     * 可用于监听动作流执行的各个阶段
     * -----------------------------------
     * 1. initialize: 初始化钩子函数
     * 2. afterInvoke: 执行完毕钩子函数
     * 3. afterInvokeNode: 执行完毕节点钩子函数
     */
    private readonly hooks: Hooks = {
        initialize: new SyncHook(['data']),
        afterInvoke: new SyncHook(['data']),
        afterInvokeNode: new SyncHook(['data'])
    }

    private lastNodeIds: string[] = []

    private readonly jobQueues: FlowJobMap = new Map()

    private readonly invokeQueueRuntimeParams: FlowNodeInvokeRuntimeParamsMap<E> = new Map()

    constructor(private readonly nodeHandlers: N, private readonly actionHandlers: A) {
        this.nodeHandlers = nodeHandlers
        this.actionHandlers = actionHandlers
    }

    public use(name: keyof Hooks, fn: (...args: unknown[]) => void, priority = 0) {
        this.hooks[name].tap(name, fn, priority)
    }

    public sequenceWorker(flow: Workflow) {
        const nodes = buildWorkFlowDataDependencies(flow)
        nodes.forEach((node, _) => {
            if (!node.right || node.right.length === 0) {
                this.lastNodeIds.push(node.id)
            }
        })
        this.jobQueues.set(flow.id, nodes)
    }

    public injectRuntimeParams(invokeId: InvokeId, runtimeParams: E) {
        this.invokeQueueRuntimeParams.set(invokeId, runtimeParams)
    }

    // 检查当前节点是否为阻断状态
    public checkUpstreamIsContainPass(nodeId: string, leftIds: string[] | undefined, runtimeParams: E) {
        if (!leftIds || leftIds.length === 0) {
            return true
        }
        const { workflow, upstreamNodesResult } = runtimeParams
        const edges = workflow?.content.edges || []
        return leftIds.some(leftId => {
            const result = upstreamNodesResult?.[leftId]

            if (!result) {
                return false
            }

            if (result.type === 'interrupted') {
                return false
            }
            if (result.type === 'condition' && result.success) {
                const edgeList = filter(item => item.source === leftId && item.target === nodeId, edges)
                const sourceHandleIds = new Set(edgeList.map(item => item?.sourceHandle).filter(Boolean))
                return result.data.some(item => sourceHandleIds.has(item.id) && item.isPass)
            }
            return result.success
        })
    }

    private async invokeTreeAsync(params: { nodes: NodeInfo; invokeId: string; flowId: string }) {
        const { nodes, invokeId, flowId } = params
        // const startNodes = [...nodes.entries()].filter(([_, node]) => !node.left || node.left.length === 0).map(([id]) => id)
        const startNode = [...nodes.values()].find(item => item.data.type === ClassNode.TRIGGER)
        if (!startNode) {
            return
        }

        const executor = new FlowExecutor(nodes, [startNode.id], async node => {
            const { nodeHandlers, hooks, lastNodeIds, invokeQueueRuntimeParams, checkUpstreamIsContainPass } = this

            const { id: nodeId, data } = node.data

            const handler = nodeHandlers[data.nodeType as K] || noop
            const runtimeParams = invokeQueueRuntimeParams.get(invokeId)

            if (!runtimeParams) {
                throw new Error('runtime context is null')
            }
            let result: FlowNodeResultADT

            // const statusNodes = nodes.map(item => runtimeParams.upstreamNodesResult[item.id])
            const isContainPass = checkUpstreamIsContainPass(nodeId, node.left, runtimeParams)
            if (!isContainPass) {
                result = {
                    type: 'interrupted',
                    success: true
                }
                // eslint-disable-next-line unicorn/consistent-destructuring
                const lastIds = this.lastNodeIds.filter(id => id !== nodeId)
                this.lastNodeIds = lastIds
                const isLastNode = lastIds.length === 0
                hooks.afterInvokeNode.call({
                    nodeId,
                    workflowId: flowId,
                    workflowInstanceId: invokeId,
                    status: isLastNode ? FlowRunStatus.SUCCESS : FlowRunStatus.RUNNING,
                    nodeStatus: {
                        status: NodeAndEdgeStatusEnum.INTERRUPTED,
                        input: [],
                        output: []
                    },
                    runtimeParams,
                    result
                })
            } else if (data.nodeType === TriggerNodeType.CLICK_TRIGGER || data.nodeType === TriggerNodeType.WEBSOCKET_TRIGGER) {
                result = {
                    type: 'args',
                    success: true,
                    data: runtimeParams.triggerArgs
                }
                // eslint-disable-next-line unicorn/consistent-destructuring
                const lastIds = this.lastNodeIds.filter(id => id !== nodeId)
                this.lastNodeIds = lastIds
                const isLastNode = lastIds.length === 0
                hooks.afterInvokeNode.call({
                    nodeId,
                    workflowId: flowId,
                    workflowInstanceId: invokeId,
                    status: isLastNode ? FlowRunStatus.SUCCESS : FlowRunStatus.RUNNING,
                    nodeStatus: {
                        status: NodeAndEdgeStatusEnum.SUCCESS,
                        input: [],
                        output: []
                    },
                    runtimeParams,
                    result
                })
            } else if (data.nodeType === FlowControlNodeType.CONDITION) {
                // @ts-expect-error expect handler to be a function
                result = await handler(node.data, runtimeParams)
                // eslint-disable-next-line unicorn/consistent-destructuring
                const lastIds = this.lastNodeIds.filter(id => id !== nodeId)
                this.lastNodeIds = lastIds
                const isLastNode = lastIds.length === 0
                if (result.type === 'condition') {
                    const payload: NodeInvokeResult<E> = {
                        nodeId,
                        workflowId: flowId,
                        workflowInstanceId: invokeId,
                        status: isLastNode ? FlowRunStatus.SUCCESS : FlowRunStatus.RUNNING,
                        nodeStatus: {
                            status: NodeAndEdgeStatusEnum.SUCCESS,
                            input: [],
                            output: []
                        },
                        runtimeParams,
                        result
                    }
                    hooks.afterInvokeNode.call(payload)
                }
            } else {
                try {
                    // @ts-expect-error expect handler to be a function
                    result = await handler(node.data, runtimeParams)
                    // eslint-disable-next-line unicorn/consistent-destructuring
                    const lastIds = this.lastNodeIds.filter(id => id !== nodeId)
                    this.lastNodeIds = lastIds
                    const isLastNode = lastIds.length === 0
                    const isEndNode = data.nodeType === EndNodeType.END

                    const nodeStatus = result.success ? NodeAndEdgeStatusEnum.SUCCESS : NodeAndEdgeStatusEnum.FAILED

                    const flowStatus =
                        nodeStatus === NodeAndEdgeStatusEnum.FAILED
                            ? FlowRunStatus.FAILED
                            : isLastNode || isEndNode
                            ? FlowRunStatus.SUCCESS
                            : FlowRunStatus.RUNNING
                    /**
                     * 执行节点后的钩子函数
                     */

                    const payload: NodeInvokeResult<E> = {
                        nodeId,
                        workflowId: flowId,
                        workflowInstanceId: invokeId,
                        status: flowStatus,
                        nodeStatus: {
                            status: nodeStatus,
                            input: [],
                            output: []
                        },
                        runtimeParams,
                        result
                    }
                    hooks.afterInvokeNode.call(payload)
                } catch (error) {
                    result = {
                        type: 'interrupted',
                        success: true
                    }
                    /**
                     * 执行节点后的钩子函数
                     */
                    const payload: NodeInvokeResult<E> = {
                        nodeId,
                        workflowId: flowId,
                        workflowInstanceId: invokeId,
                        status: FlowRunStatus.FAILED,
                        nodeStatus: {
                            status: NodeAndEdgeStatusEnum.FAILED,
                            input: [],
                            output: []
                        },
                        runtimeParams,
                        result
                    }
                    hooks.afterInvokeNode.call(payload)
                    // @ts-expect-error expect error to have message
                    error?.message !== 'disabled' && Toast.error(error?.message)
                    // return
                    throw error
                }
            }
            const newRunTimeParams = invokeQueueRuntimeParams.get(invokeId)
            if (newRunTimeParams) {
                this.injectRuntimeParams(invokeId, {
                    ...newRunTimeParams,
                    upstreamNodesResult: { ...newRunTimeParams.upstreamNodesResult, [nodeId]: result }
                })
            }

            return result
        })

        await executor.run()
        const results = executor.getResults()
        const endNode = [...nodes.values()].find(item => item.data.type === ClassNode.END)
        if (endNode?.id) {
            const argsResult = results.get(endNode?.id)
            if (argsResult?.type === 'args' && argsResult.success) {
                return argsResult.data
            }
            return undefined
        }
    }

    /**
     * 动作流执行入口（flow 专用）
     */
    public async invokeFlow(id: string, invokeId: InvokeId, pageId?: string) {
        const jobs = this.jobQueues.get(id)
        if (jobs) {
            // 执行初始化钩子
            await this.hooks.initialize.call({
                flowId: id,
                invokeId,
                pageId
            })
            return await this.invokeTreeAsync({
                nodes: jobs,
                invokeId,
                flowId: id
            })
        }
    }

    /**
     * 线性动作列表执行（无日志、无队列，仅顺序执行节点）
     */
    public async invokeList(actions: IndependentAction[], runtimeParams: E, options?: ListInvokeOptions<E>) {
        if (!actions || actions.length === 0) {
            return
        }

        let currentParams: E = {
            ...runtimeParams,
            upstreamNodesResult: runtimeParams.upstreamNodesResult || {}
        }

        for (const action of actions) {
            const shouldRun = (await options?.shouldExecute?.(action, currentParams)) ?? true
            if (!shouldRun) {
                const continueResult: ActionResultADT = {
                    type: 'continue',
                    success: true
                }
                currentParams = {
                    ...currentParams,
                    upstreamNodesResult: {
                        ...currentParams.upstreamNodesResult,
                        [action.id]: continueResult
                    }
                }
                continue
            }

            const handler = this.actionHandlers[action.type as K2] || noop
            // const handlerFn = handler as unknown as (node: FlowNode, runtimeParams: E) => Promise<FlowNodeResultADT> | FlowNodeResultADT
            try {
                // @ts-expect-error expect handler to be a function
                const result = await handler(action.data[action.type], currentParams)
                currentParams = {
                    ...currentParams,
                    upstreamNodesResult: {
                        ...currentParams.upstreamNodesResult,
                        [action.id]: result
                    }
                }

                if (options?.stopOnError && result?.type === 'interrupted') {
                    return currentParams.upstreamNodesResult
                }
            } catch (error) {
                const interruptedResult: FlowNodeResultADT = {
                    type: 'interrupted',
                    success: true
                }
                currentParams = {
                    ...currentParams,
                    upstreamNodesResult: {
                        ...currentParams.upstreamNodesResult,
                        [action.id]: interruptedResult
                    }
                }
                const onError = options?.onError
                if (onError) {
                    await onError(error, { action, runtimeParams: currentParams })
                }
                if (options?.stopOnError) {
                    throw error
                }
            }
        }

        return currentParams.upstreamNodesResult
    }
}
